/**
 * @file H5 渠道来源采集工具
 * @description 统一采集用户进入 H5 应用的渠道信息（URL 显式渠道参数 entryChannel/entryScene），
 * 供自研 Analytics 埋点链路自动附加渠道参数使用。
 * 注意：entryChannel/entryScene 是独立的埋点渠道体系，与业务渠道码 sourceFlag（登录/下单归因）完全解耦。
 * 上报出口字段名与小程序 journey-client-wx 对齐：referer（取值即采集层 entryChannel）/ entryScene，
 * 便于后端 /stat/mark 跨端 union 统计。
 */

import { trackPageView } from './analytics'

/** sessionStorage key：渠道来源信息（按访问会话，整页加载时整体覆盖） */
const CHANNEL_SOURCE_KEY = 'channelSourceInfo'
/** localStorage key：进入事件上次上报时间戳（跨整页加载去抖） */
const ENTRY_TRACK_TS_KEY = 'h5EntryLastReportTs'
/** 进入事件会话去抖间隔（30 分钟） */
const SESSION_GAP_MS = 30 * 60 * 1000
/** 进入 H5 应用事件名 */
const ENTRY_EVENT_NAME = 'H5_EntryExposure'

export interface ChannelSourceInfo {
  /** 流量来源渠道（外部入口 entryChannel 参数携带，无则为空；与业务 sourceFlag 无关） */
  entryChannel: string
  /** 流量来源业务场景（外部入口 entryScene 参数携带，如来源方轮播图/菜单位，无则为空） */
  entryScene: string
}

/** 安全 decode，异常时原样返回 */
function safeDecode(val: string): string {
  try {
    return decodeURIComponent(val)
  } catch (e) {
    return val
  }
}

/**
 * 合并解析 location.search 与 hash 路由问号后参数，兼容 history / hash 两种路由
 */
function getMergedQuery(): Record<string, string> {
  const result: Record<string, string> = {}
  const collect = (search: string) => {
    if (!search) return
    const qs = search.startsWith('?') ? search.slice(1) : search
    qs.split('&').forEach((pair) => {
      if (!pair) return
      const idx = pair.indexOf('=')
      const k = idx >= 0 ? pair.slice(0, idx) : pair
      const v = idx >= 0 ? pair.slice(idx + 1) : ''
      if (k) result[k] = safeDecode(v)
    })
  }
  try {
    collect(window.location.search)
    // hash 路由：#/path?a=b
    const hash = window.location.hash || ''
    const hashQIndex = hash.indexOf('?')
    if (hashQIndex >= 0) collect(hash.slice(hashQIndex + 1))
  } catch (e) {
    console.warn('解析 URL 渠道参数失败:', e)
  }
  return result
}

/** 从 URL 解析埋点渠道/业务场景参数（独立埋点参数，不复用业务 channel/sourceflag） */
function parseEntryParamsFromUrl(): {
  entryChannel: string
  entryScene: string
} {
  const q = getMergedQuery()
  return {
    entryChannel: q.entryChannel || q.entrychannel || '',
    entryScene: q.entryScene || q.entryscene || ''
  }
}

/** 读取 sessionStorage 中的渠道来源信息 */
function readChannelSourceInfo(): Partial<ChannelSourceInfo> {
  try {
    const cached = sessionStorage.getItem(CHANNEL_SOURCE_KEY)
    if (!cached) return {}
    return JSON.parse(cached) as Partial<ChannelSourceInfo>
  } catch (e) {
    return {}
  }
}

/**
 * 采集渠道来源信息并写入 sessionStorage
 * 每次整页加载（进入应用）时调用，整体覆盖
 */
export function captureChannelSource(): void {
  try {
    const parsed = parseEntryParamsFromUrl()
    const info: ChannelSourceInfo = {
      entryChannel: parsed.entryChannel,
      entryScene: parsed.entryScene
    }
    sessionStorage.setItem(CHANNEL_SOURCE_KEY, JSON.stringify(info))
  } catch (e) {
    console.warn('采集渠道来源信息失败:', e)
  }
}

/**
 * 获取渠道来源原始字段（供业务/透传等链路使用，保留 entryChannel 字段名）
 * 无外部来源时为空字符串，不兜底任何业务渠道值
 */
export function getChannelSourceParams(): {
  entryChannel: string
  entryScene: string
} {
  const info = readChannelSourceInfo()
  return {
    entryChannel: info.entryChannel || '',
    entryScene: info.entryScene || ''
  }
}

/**
 * 获取埋点上报用渠道参数（供 analytics 自动附加）
 * 上报接口入参约定：流量来源渠道字段名为 referer（取值即采集层 entryChannel），entryScene 字段名不变
 */
export function getTrackChannelParams(): {
  referer: string
  entryScene: string
} {
  const { entryChannel, entryScene } = getChannelSourceParams()
  return {
    referer: entryChannel,
    entryScene
  }
}

/**
 * 判断本次进入是否为外部渠道主动进入（URL 携带显式渠道/场景参数），
 * 供进入埋点跳过会话去抖使用：外部渠道每次进入都需记一次 PV
 */
export function isExternalChannelEntry(): boolean {
  const { entryChannel, entryScene } = parseEntryParamsFromUrl()
  return !!(entryChannel || entryScene)
}

/**
 * 进入 H5 应用埋点（渠道 PV/UV）
 * 外部渠道进入必报；其余按 30 分钟 localStorage 时间戳去抖，防刷新/直接访问刷量。
 * 渠道字段由 Analytics 公共参数自动附加，全程 try/catch，埋点失败不影响业务
 */
export function trackAppEntry(): void {
  try {
    if (!isExternalChannelEntry()) {
      const lastTs = Number(localStorage.getItem(ENTRY_TRACK_TS_KEY)) || 0
      if (Date.now() - lastTs < SESSION_GAP_MS) return
    }
    trackPageView(ENTRY_EVENT_NAME)
    localStorage.setItem(ENTRY_TRACK_TS_KEY, String(Date.now()))
  } catch (e) {
    console.warn('进入 H5 应用埋点上报失败:', e)
  }
}
